
// Utility functions for lyrics management

// Custom event for lyrics changes
export const LYRICS_CHANGED_EVENT = 'sonicGrooveLyricsChanged';

export const dispatchLyricsChangedEvent = (detail = {}) => {
  console.log(`🎵 Dispatching custom event: ${LYRICS_CHANGED_EVENT}`, detail);
  window.dispatchEvent(new CustomEvent(LYRICS_CHANGED_EVENT, { detail }));
};

export const loadLyricsFromUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  const lyricsData = await response.json();
  
  if (!lyricsData.lyrics || !Array.isArray(lyricsData.lyrics)) {
    throw new Error('Invalid lyrics format: missing lyrics array');
  }

  const isValidFormat = lyricsData.lyrics.every(lyric => 
    typeof lyric.text === 'string' &&
    typeof lyric.startTime === 'number' &&
    typeof lyric.endTime === 'number'
  );

  if (!isValidFormat) {
    throw new Error('Invalid lyrics format: each lyric must have text, startTime, and endTime');
  }

  return lyricsData;
};

export const loadLyricsFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const lyricsData = JSON.parse(e.target.result);
        
        if (!lyricsData.lyrics || !Array.isArray(lyricsData.lyrics)) {
          throw new Error('Invalid lyrics format: missing lyrics array');
        }

        const isValidFormat = lyricsData.lyrics.every(lyric => 
          typeof lyric.text === 'string' &&
          typeof lyric.startTime === 'number' &&
          typeof lyric.endTime === 'number'
        );

        if (!isValidFormat) {
          throw new Error('Invalid lyrics format: each lyric must have text, startTime, and endTime');
        }

        resolve(lyricsData);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.readAsText(file);
  });
};

export const getSongFilename = (song) => {
  if (!song) return null;
  if (song.file && song.file.name) return song.file.name;
  if (song.filename) return song.filename;
  if (song.name) return song.name; 
  if (song.title && /\.(mp3|m4a|wav|ogg|flac|aac|ost)$/i.test(song.title)) return song.title;
  if (song.title && song.artist) return `${song.artist} - ${song.title}`;
  if (song.title) return song.title; 
  
  return null;
};

export const saveLyricsToStorage = (song, lyricsData) => {
  const filenameKey = getSongFilename(song);
  if (!filenameKey) {
    console.warn('Cannot save lyrics: no filename key found for song', song);
    return;
  }
  
  try {
    const savedLyrics = JSON.parse(localStorage.getItem('sonicGrooveLyrics') || '{}');
    
    if (!lyricsData.metadata) {
      lyricsData.metadata = {};
    }
    if (!lyricsData.metadata.sourceFiles) {
      lyricsData.metadata.sourceFiles = {};
    }
    if (!lyricsData.metadata.sourceFiles.audio) {
      lyricsData.metadata.sourceFiles.audio = filenameKey;
    }
    if (!lyricsData.metadata.generatedAt) {
      lyricsData.metadata.generatedAt = new Date().toISOString();
    }

    savedLyrics[filenameKey] = lyricsData;
    localStorage.setItem('sonicGrooveLyrics', JSON.stringify(savedLyrics));
    console.log(`🎵 Saved lyrics for key: "${filenameKey}"`);
    dispatchLyricsChangedEvent({ action: 'saved', songKey: filenameKey });
  } catch (error) {
    console.warn('Failed to save lyrics to storage:', error);
  }
};

export const loadLyricsFromStorage = (song) => {
  const filenameKey = getSongFilename(song);
  if (!filenameKey) {
    console.warn('Cannot load lyrics: no filename key found for song', song);
    return null;
  }
  
  try {
    const savedLyrics = JSON.parse(localStorage.getItem('sonicGrooveLyrics') || '{}');
    const lyrics = savedLyrics[filenameKey] || null;
    console.log(`🎵 Attempting to load lyrics for key: "${filenameKey}". Found: ${!!lyrics}`);
    return lyrics;
  } catch (error) {
    console.warn('Failed to load lyrics from storage:', error);
    return null;
  }
};

export const clearLyricsFromStorage = (song) => {
  const filenameKey = getSongFilename(song);
  if (!filenameKey) {
    console.warn('Cannot clear lyrics: no filename key found for song', song);
    return false;
  }
  
  try {
    const savedLyrics = JSON.parse(localStorage.getItem('sonicGrooveLyrics') || '{}');
    let cleared = false;
    
    if (savedLyrics[filenameKey]) {
      delete savedLyrics[filenameKey];
      cleared = true;
    }
    
    Object.keys(savedLyrics).forEach(key => {
      const lyricsData = savedLyrics[key];
      if (lyricsData.metadata?.sourceFiles?.audio === filenameKey) {
        delete savedLyrics[key];
        cleared = true;
        console.log(`🎵 Also cleared lyrics for key "${key}" due to metadata match with "${filenameKey}"`);
      }
    });
      
    if (cleared) {
      localStorage.setItem('sonicGrooveLyrics', JSON.stringify(savedLyrics));
      console.log(`🎵 Cleared lyrics related to key: "${filenameKey}"`);
      dispatchLyricsChangedEvent({ action: 'cleared', songKey: filenameKey });
    }
    return cleared;
  } catch (error) {
    console.warn('Failed to clear lyrics from storage:', error);
    return false;
  }
};

export const findLyricsByAudioFile = (song) => {
  const filenameKey = getSongFilename(song);
  if (!filenameKey) return null;
  
  try {
    const savedLyrics = JSON.parse(localStorage.getItem('sonicGrooveLyrics') || '{}');
    
    if (savedLyrics[filenameKey]) {
      console.log(`🎵 findLyricsByAudioFile: Direct match found for key "${filenameKey}"`);
      return savedLyrics[filenameKey];
    }
    
    for (const [_key, lyricsData] of Object.entries(savedLyrics)) {
      if (lyricsData.metadata && lyricsData.metadata.sourceFiles && lyricsData.metadata.sourceFiles.audio) {
        const audioFilenameInMetadata = lyricsData.metadata.sourceFiles.audio;
        if (audioFilenameInMetadata === filenameKey) {
          console.log(`🎵 findLyricsByAudioFile: Metadata match found for key "${filenameKey}" (stored under key "${_key}")`);
          return lyricsData;
        }
      }
    }
    console.log(`🎵 findLyricsByAudioFile: No lyrics found for key "${filenameKey}"`);
    return null;
  } catch (error) {
    console.warn('Failed to find lyrics by audio file:', error);
    return null;
  }
};

export const hasLyrics = (song) => {
  return !!findLyricsByAudioFile(song);
};

export const purgeAllLyrics = () => {
  try {
    const oldValue = localStorage.getItem('sonicGrooveLyrics');
    localStorage.removeItem('sonicGrooveLyrics');
    console.log('🎵 Purged all lyrics data from storage');
    
    // Standard storage event
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'sonicGrooveLyrics',
      oldValue: oldValue,
      newValue: null,
      storageArea: localStorage,
    }));
    // Custom event for immediate reaction
    dispatchLyricsChangedEvent({ action: 'purgedAll' });
    return true;
  } catch (error) {
    console.warn('Failed to purge lyrics data:', error);
    return false;
  }
};

export const getAllStoredLyrics = () => {
  try {
    const savedLyrics = JSON.parse(localStorage.getItem('sonicGrooveLyrics') || '{}');
    return savedLyrics;
  } catch (error) {
    console.warn('Failed to get all stored lyrics:', error);
    return {};
  }
};
